import { useMemo } from "react";

import { type CheckoutVariantPriceStats } from "@fitness-app/data-models/entities/CheckoutVariant";
import { getOrderDiscountBreakdown, getOrderRevenueBreakdown } from "@fitness-app/data-models/entities/Order";

import { useCheckoutVariantOrders } from "./useCheckoutVariantOrders";

/**
 * Statystyki cen wariantu liczone wprost z opłaconych zamówień.
 *
 * Licznik `checkout_variant.pricesStats` trzyma wyłącznie sumę kwot, więc liczba
 * transakcji musiała być z niej wyprowadzana dzieleniem przez cenę katalogową —
 * co przy każdym rabacie (a już zwłaszcza przy kodzie 100% na pierwszą ratę)
 * dawało zero klientek. Zamówienia są tu jedynym źródłem prawdy.
 *
 * Rabaty są raportowane osobno: zamówienie z kodem 100% jest opłacone na 0 zł, więc
 * wchodzi do `transactions` jak sprzedaż — `freeTransactions` mówi, ile z nich to gratisy.
 */
export const useCheckoutVariantPricesStats = (variantId: string, productId: string) => {
  const { data: paidOrders } = useCheckoutVariantOrders([variantId], productId, null);

  return useMemo(() => {
    return (paidOrders ?? []).reduce<Record<string, CheckoutVariantPriceStats>>((acc, order) => {
      if (!order.priceId) {
        return acc;
      }

      const { paid, projected } = getOrderRevenueBreakdown(order);
      const { discountAmount, isFree } = getOrderDiscountBreakdown(order);
      const current = acc[order.priceId];

      acc[order.priceId] = {
        amount: (current?.amount ?? 0) + paid,
        projected: (current?.projected ?? 0) + projected,
        transactions: (current?.transactions ?? 0) + 1,
        freeTransactions: (current?.freeTransactions ?? 0) + (isFree ? 1 : 0),
        discountAmount: (current?.discountAmount ?? 0) + discountAmount,
      };

      return acc;
    }, {});
  }, [paidOrders]);
};
